import { useEffect, useRef, useState, useSyncExternalStore, type ReactElement } from "react";
import { moderationText, type ModerationAction } from "odyssey-shared";
import { ModalWindow, Tabs, logical, useLogicalSize, useScrollCue, type TabEntry } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { muteTime } from "../chat/words.ts";
import { ActionFields, ActionFooter, Refused, endsSentence, type FormText } from "./action-form.tsx";
import type { ListedRow } from "./client.ts";
import type { ModerationDesk } from "./desk.ts";
import { BanConfirm, EndConfirm, type EndAsked } from "./dialogs.tsx";
import type { FamilyModel } from "./family.ts";
import type { LookupModel } from "./lookup.ts";
import { FeedbackTab } from "./feedback-tab.tsx";
import { DEFAULT_LENGTH, MODERATION_LAYOUT, WIDE_CONTENT, chosenMinutes, isTimed, lengthWords, moderationWindowBox } from "./layout.ts";
import { windowPlace, windowSize } from "../windows/places.ts";
import { OnlineColumn } from "./online-column.tsx";
import type { OnlineModel } from "./online.ts";
import { PlayerTab } from "./player-tab.tsx";
import type { StaffModel } from "./staff.ts";
import { StaffTab } from "./staff-tab.tsx";
import { HistoryTab, InForceTab } from "./records.tsx";
import { whenWords } from "./words.ts";

/**
 * The Moderation window (ADR-056's amendments of 2026-10-04, item 10, and of 2026-10-06, decisions 3, 6, 7 and 14;
 * the design's `moderation-window` card, a visual reference): the kit's modal window over the game, shown while the
 * desk says it is open. Wide, the Online players column sits beside the tabs; narrow, where 612 logical pixels do not
 * fit, Online is the first tab, and the one shown while no family is chosen and no tab has been. The tabs are Player (who the family is, what stands on it, and the actions), In force
 * (every ban and freeze that stands), History (everything recorded) and Feedback (the family's access to feedback), and, for a
 * lead alone (the offer's word `staff`), Staff (the staff accounts and the badge each shows, P1.9-T207).
 *
 * A GM finds a player in the list or types a family, looks at them, acts, and checks. Choosing an action shows its
 * fields and a pinned footer; Ban asks to confirm; End ban and End freeze ask for a staff-only reason; recording
 * disables the fields; a written action shows what was done and reads the list, the family and the records again; a
 * refusal keeps every field and shows the gateway's sentence. The window moves and resizes (P1.9-T184: from 300 by 236,
 * its narrow layout, to 900 by 520, kept per device under `moderation`), and which layout it draws follows the width it
 * is given. It is a modal (`ModalWindow`), the router's
 * topmost layer: Escape empties the search while the search field holds text and has focus, and otherwise closes
 * the window, as its close button does. The component imports no stylesheet, so it renders to static markup under
 * `bun test`; `mount.tsx` imports the sheets.
 */

/**
 * The tabs, by id.
 */
export type ModerationTab = "online" | "player" | "inforce" | "history" | "feedback" | "staff";

/**
 * Where the window starts when it first shows, which the gallery uses to draw each of the card's states: the tab, the
 * action chosen and its text, a dialog open (the ban's confirmation, or the end of a
 * standing row by its id), and whether the window is drawn narrow whatever the room.
 */
export interface ModerationWindowStart {
  readonly tab?: ModerationTab;
  readonly chosen?: ModerationAction;
  readonly text?: Partial<FormText>;
  readonly confirming?: boolean;
  readonly ending?: EndAsked;
  readonly narrow?: boolean;
}

/**
 * What the window runs over: the desk, the list of who is online, the family's read, the family lookup, the GM's own
 * family when the
 * page knows it, the writer of moments (`whenWords` unless a test gives another), and where it starts.
 */
export interface ModerationWindowProps {
  readonly desk: ModerationDesk;
  readonly online: OnlineModel;
  readonly family: FamilyModel;
  readonly lookup: LookupModel;
  readonly staff: StaffModel;
  readonly self?: () => string | null;
  readonly when?: (at: number) => string;
  readonly start?: ModerationWindowStart;
}

const PANEL_ID = "ot-moderation-panel";
const EMPTY_TEXT: FormText = Object.freeze({ length: "", reason: "", note: "" });

/**
 * A tab's element id.
 *
 * @param id The tab.
 * @returns The id.
 */
function tabId(id: ModerationTab): string {
  return `ot-moderation-tab-${id}`;
}

/**
 * The Moderation window, following the desk.
 *
 * @param props The desk, the list, the family's read, the GM's own family and the writer of moments.
 * @returns The window over its scrim and its dialogs, or null while the desk is closed or the gateway offered
 *   nothing.
 * @throws Error when it shows outside a `KitInputProvider`.
 * @example
 * <ModerationWindow desk={desk} online={online} family={family} />
 */
export function ModerationWindow({ desk, online, family, lookup, staff, self = () => null, when = whenWords, start = {} }: ModerationWindowProps): ReactElement | null {
  const snapshot = useSyncExternalStore(desk.subscribe, desk.getSnapshot, desk.getSnapshot);
  const list = useSyncExternalStore(online.subscribe, online.getSnapshot, online.getSnapshot);
  const read = useSyncExternalStore(family.subscribe, family.getSnapshot, family.getSnapshot);
  const found = useSyncExternalStore(lookup.subscribe, lookup.getSnapshot, lookup.getSnapshot);
  const staffed = useSyncExternalStore(staff.subscribe, staff.getSnapshot, staff.getSnapshot);
  const content = useRef<HTMLDivElement>(null);
  const size = useLogicalSize(content, snapshot.open);
  const bodyCue = useScrollCue();
  const [tab, setTab] = useState<ModerationTab | null>(start.tab ?? null);
  const [chosen, setChosen] = useState<ModerationAction | null>(start.chosen ?? null);
  const [text, setText] = useState<FormText>({ ...EMPTY_TEXT, ...start.text });
  const [confirming, setConfirming] = useState(start.confirming ?? false);
  const [ending, setEnding] = useState<EndAsked | null>(start.ending ?? null);
  const [endReason, setEndReason] = useState("");
  const [fault, setFault] = useState<string | null>(null);
  const [pending, setPending] = useState<ModerationAction | null>(null);
  const written = useRef(snapshot.written);

  useEffect(() => {
    if (snapshot.written === written.current) {
      return;
    }
    written.current = snapshot.written;
    online.refresh();
    family.refresh();
    setChosen(null);
    setConfirming(false);
    setEnding(null);
    setEndReason("");
    setText((current) => ({ ...current, reason: "", note: "" }));
  }, [snapshot.written, online, family]);

  useEffect(() => {
    if (!snapshot.busy && snapshot.error !== null) {
      setConfirming(false);
    }
  }, [snapshot.busy, snapshot.error]);

  const staffShown = snapshot.open && snapshot.staff && tab === "staff";
  useEffect(() => {
    if (staffShown) {
      staff.open();
      return () => staff.close();
    }
    return undefined;
  }, [staffShown, staff]);

  if (!snapshot.open || snapshot.offered.length === 0) {
    return null;
  }
  const wide = start.narrow !== true && (size === null || size.width >= WIDE_CONTENT);
  const shown: ModerationTab = tab === null ? (!wide && read.name === null ? "online" : "player") : wide && tab === "online" ? "player" : tab === "staff" && !snapshot.staff ? "player" : tab;
  const now = Date.now();
  const name = read.read?.family ?? read.name ?? "";
  const busy = snapshot.busy;
  const refused = pending !== null && !busy && snapshot.error !== null ? snapshot.error : null;
  const done = pending !== null && !busy && snapshot.error === null && snapshot.status !== null ? { title: say(`moderation.window.done.${pending}`), sentence: snapshot.status } : null;
  const timed = chosen !== null && isTimed(chosen) ? chosen : null;
  const length = timed === null ? "" : text.length === "" ? DEFAULT_LENGTH[timed] : text.length;
  const chosenLength = timed === null ? undefined : chosenMinutes(timed, length);
  const minutes = chosenLength === 0 && !snapshot.permanent ? null : chosenLength;
  const ends = timed === null ? null : endsSentence(timed, length, now, when);

  /**
   * Opens a family on the Player tab: the list selects it, the form names it and the family is read.
   *
   * @param chosenFamily The family.
   * @param toPlayer Whether to move to the Player tab.
   */
  const openFamily = (chosenFamily: string, toPlayer: boolean): void => {
    pickFamily(chosenFamily);
    if (toPlayer || !wide) {
      setTab("player");
    }
  };

  /**
   * Closes the open file: no family is selected or read, the list's highlight goes, the form's typed text is dropped
   * (closing a file means done with it), and the Player tab asks for a
   * player again, narrow too, where nothing chosen would otherwise show the Online tab; focus moves to the Player
   * tab, since the close button goes with the file.
   */
  const closeFile = (): void => {
    pickFamily(null);
    setText(EMPTY_TEXT);
    setTab("player");
    requestAnimationFrame(() => document.getElementById(tabId("player"))?.focus());
  };

  /**
   * Selects a family, or none, in the list and the read, and closes the chosen action and the last answer, as
   * opening another family and closing the file both do. The form's text is kept.
   *
   * @param chosenFamily The family, or null.
   */
  const pickFamily = (chosenFamily: string | null): void => {
    online.select(chosenFamily);
    family.choose(chosenFamily);
    setChosen(null);
    setPending(null);
    setFault(null);
    desk.clearAnswer();
  };

  /**
   * Chooses an action, which shows its fields, or none, which closes them; the last answer is forgotten.
   *
   * @param action The action, or null.
   */
  const choose = (action: ModerationAction | null): void => {
    setChosen(action);
    setPending(null);
    setFault(null);
    setText((current) => ({ ...current, length: "" }));
    desk.clearAnswer();
  };

  /**
   * Sends the chosen action, once the page's own checks hold: a reason of up to 200 characters on one line, and a
   * length the action takes.
   *
   * @param confirmed Whether a ban has been confirmed.
   */
  const go = (confirmed: boolean): void => {
    if (chosen === null || busy) {
      return;
    }
    if (moderationText(text.reason) === null) {
      setFault(say("moderation.desk.reason"));
      return;
    }
    if (minutes === null) {
      setFault(say("moderation.window.lengthOff"));
      return;
    }
    setFault(null);
    if (chosen === "ban" && !confirmed) {
      setConfirming(true);
      return;
    }
    setPending(chosen);
    desk.submit({ action: chosen, familyName: name, minutes, reason: text.reason, note: chosen === "alert" || chosen.startsWith("feedback") ? "" : text.note });
  };

  /**
   * Ends a ban or a freeze from its row, once its reason holds.
   */
  const end = (): void => {
    if (ending === null || busy) {
      return;
    }
    if (moderationText(endReason) === null) {
      setFault(say("moderation.desk.reason"));
      return;
    }
    setFault(null);
    setPending("lift");
    desk.submit({ action: "lift", familyName: ending.family, minutes: undefined, reason: endReason, note: "", lifts: ending.id });
  };

  /**
   * Asks to end a row: the dialog takes its reason.
   *
   * @param row The row's id, kind, family and end.
   */
  const askEnd = (row: { readonly id: string; readonly action: "ban" | "freeze"; readonly family: string; readonly until: number | null }): void => {
    setEnding({ id: row.id, action: row.action, family: row.family, left: row.until === null ? say("moderation.window.permanent") : muteTime(Math.max(0, row.until - now)) });
    setEndReason("");
    setFault(null);
    setPending(null);
    desk.clearAnswer();
  };

  /**
   * Closes the window, as its close button and Escape ask; Escape in the search field with text empties the search
   * instead.
   */
  const close = (): void => {
    const focused = document.activeElement;
    if (list.text !== "" && focused instanceof HTMLInputElement && focused.closest("[data-moderation='online']") !== null) {
      online.type("");
      return;
    }
    setConfirming(false);
    setEnding(null);
    desk.close();
  };

  const tabs: TabEntry<ModerationTab>[] = [
    ...(wide ? [] : [{ id: "online" as const, label: say("moderation.window.tabs.online"), name: say("moderation.window.tabs.online") }]),
    { id: "player", label: say("moderation.window.tabs.player"), name: say("moderation.window.tabs.player") },
    { id: "inforce", label: say("moderation.window.tabs.inforce"), name: snapshot.standingRead ? say("moderation.window.tabCounted", { tab: { k: "moderation.window.tabs.inforce" }, count: String(snapshot.standing.length) }) : say("moderation.window.tabs.inforce"), badge: snapshot.standingRead && snapshot.standing.length > 0 ? String(snapshot.standing.length) : null },
    { id: "history", label: say("moderation.window.tabs.history"), name: say("moderation.window.tabs.history") },
    { id: "feedback", label: say("moderation.window.tabs.feedback"), name: say("moderation.window.tabs.feedback") },
    ...(snapshot.staff ? [{ id: "staff" as const, label: say("moderation.window.tabs.staff"), name: say("moderation.window.tabs.staff") }] : []),
  ];
  const column = (
    <OnlineColumn
      snapshot={list}
      lookup={found}
      onType={(text) => {
        online.type(text);
        lookup.type(text);
      }}
      onSelect={(chosenFamily, enter) => {
        openFamily(chosenFamily, enter);
        if (enter) {
          requestAnimationFrame(() => document.getElementById(tabId("player"))?.focus());
        }
      }}
      onMore={online.more}
    />
  );
  const mapName = (id: string): string => list.mapNames[id] ?? id;
  const acting = chosen !== null && (shown === "player" || (shown === "feedback" && chosen.startsWith("feedback")));
  const footerLabel = chosen === null ? "" : chosen === "ban" ? (minutes === 0 ? say("moderation.window.go.banPermanent") : say("moderation.window.go.ban", { length: lengthWords(minutes ?? 0) })) : chosen === "freeze" ? say("moderation.window.go.freeze", { length: lengthWords(minutes ?? 0) }) : chosen === "warn" || chosen === "kick" ? say(`moderation.window.go.${chosen}`, { family: name }) : chosen === "alert" ? say("moderation.window.go.alert") : chosen === "feedbackpause" ? say("moderation.window.feedbackGo.feedbackpause", { length: lengthWords(minutes ?? 0) }) : chosen === "feedbackresume" || chosen === "feedbackoff" || chosen === "feedbackon" ? say(`moderation.window.feedbackGo.${chosen}`) : say(`moderation.actions.${chosen}`);
  const body =
    shown === "online" ? (
      column
    ) : shown === "player" ? (
      <>
        <PlayerTab
          family={read}
          offered={snapshot.offered}
          chosen={chosen !== null && chosen.startsWith("feedback") ? null : chosen}
          busy={busy}
          narrow={!wide}
          done={done}
          now={now}
          when={when}
          mapName={mapName}
          onChoose={choose}
          onClose={closeFile}
          onEnd={(row) => askEnd({ id: row.id, action: row.action, family: name, until: row.until })}
          onFeedback={() => setTab("feedback")}
          onHistory={() => setTab("history")}
        />
        {acting && chosen !== null ? <ActionFields action={chosen} family={name} text={{ ...text, length }} onText={(change) => setText((current) => ({ ...current, ...change }))} disabled={busy} narrow={!wide} ends={ends} refused={refused} permanent={snapshot.permanent} /> : null}
        {fault !== null && ending === null ? <Refused sentence={fault} /> : null}
      </>
    ) : shown === "inforce" ? (
      <InForceTab snapshot={snapshot} lifts={snapshot.offered.includes("lift")} now={now} onEnd={(row: ListedRow) => askEnd({ id: row.id, action: row.action === "ban" ? "ban" : "freeze", family: row.familyName, until: row.until })} onOpen={(chosenFamily) => openFamily(chosenFamily, true)} />
    ) : shown === "history" ? (
      <HistoryTab snapshot={snapshot} now={now} onMore={desk.more} />
    ) : shown === "staff" && snapshot.staff ? (
      <StaffTab snapshot={staffed} onSelect={staff.select} onDesignate={staff.designate} onRefresh={staff.refresh} />
    ) : (
      <>
        <FeedbackTab family={read} offered={snapshot.offered} chosen={chosen} text={{ ...text, length }} onText={(change) => setText((current) => ({ ...current, ...change }))} busy={busy} narrow={!wide} now={now} when={when} ends={ends} onChoose={choose} />
        {refused !== null && chosen !== null && chosen.startsWith("feedback") ? <Refused sentence={refused} /> : null}
        {fault !== null && ending === null ? <Refused sentence={fault} /> : null}
      </>
    );
  const pane = (
    <div className="ot-moderation__pane" data-moderation="pane">
      <div className="ot-moderation__tabs ot-scroll">
        <Tabs
          label={say("moderation.window.tabsLabel")}
          tabs={tabs}
          value={shown}
          onChange={(next) => {
            setTab(next);
            if (next === "history") {
              desk.show("all");
            }
          }}
          tabId={tabId}
          panelId={PANEL_ID}
        />
      </div>
      <div ref={bodyCue} id={PANEL_ID} role="tabpanel" aria-labelledby={tabId(shown)} className={shown === "online" ? "ot-moderation__body ot-moderation__body--online" : "ot-moderation__body ot-scroll"} data-moderation="body" style={{ gap: logical(MODERATION_LAYOUT.gap) }}>
        {body}
      </div>
      {acting && chosen !== null ? <ActionFooter self={self()} label={footerLabel} danger={chosen === "ban" || chosen === "feedbackoff"} busy={busy} slow={snapshot.slow} refused={refused !== null} onCancel={() => choose(null)} onGo={() => go(false)} /> : null}
    </div>
  );
  const firsts = read.read?.characters.map((character) => character.first) ?? [];
  return (
    <>
      <ModalWindow id="moderation" place={windowPlace("moderation")} size={windowSize("moderation")} title={say("moderation.window.title")} onClose={close} fit={(area) => moderationWindowBox(area, start.narrow === true)}>
        <div ref={content} className={wide ? "ot-moderation ot-moderation--wide" : "ot-moderation ot-moderation--narrow"} data-moderation="window" style={{ gap: logical(MODERATION_LAYOUT.columnGap) }}>
          {wide ? (
            <>
              <div className="ot-moderation__column" style={{ width: logical(MODERATION_LAYOUT.column) }}>
                {column}
              </div>
              <span className="ot-moderation__rule" aria-hidden="true" />
            </>
          ) : null}
          {pane}
        </div>
      </ModalWindow>
      {confirming && chosen === "ban" ? <BanConfirm asked={{ family: name, firsts, length: lengthWords(minutes ?? 0), ends: minutes === 0 || minutes === undefined || minutes === null ? null : when(now + minutes * 60_000), reason: text.reason, note: text.note }} busy={busy} onConfirm={() => go(true)} onBack={() => setConfirming(false)} /> : null}
      {ending === null ? null : <EndConfirm asked={ending} reason={endReason} onReason={setEndReason} busy={busy} error={fault ?? (pending === "lift" ? (refused ?? undefined) : undefined)} onConfirm={end} onBack={() => setEnding(null)} />}
    </>
  );
}
